import { useMemo } from 'react';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { QueryOptions } from 'react-query';
import { useGetUnifiedThreadEnabled, useGetFusionEnabled } from '../utils';
import { isCallRecordItem, isSmsMessageItem, isAiActivityItem } from '../utils/thread-items-utils';
import { useGetThreadQuery as useGetThreadV3Query } from '../v3/queries';
import { useGetUnifiedThreadQuery as useGetUnifiedThreadV4Query } from '../v4/queries';

type UseGetThreadQueryDataProps = {
  threadId: string;
  locationId: string;
  focusItemId?: string;
  focusItemCreatedAt?: string;
  enabled?: boolean;
  retryLimit?: QueryOptions['retry'];
  cacheTime?: number;
};

export const useGetThreadQueryData = ({
  threadId,
  locationId,
  focusItemId,
  focusItemCreatedAt,
  enabled,
  retryLimit,
  cacheTime,
}: UseGetThreadQueryDataProps) => {
  const getUnifiedThreadV4Enabled = useGetUnifiedThreadEnabled([locationId]);
  const fusionEnabled = useGetFusionEnabled([locationId]);

  const v3Request = useMemo(
    () => ({
      threadId,
      locationId,
      taggedSmsId: focusItemId,
      taggedCreatedAt: focusItemCreatedAt,
      messageLimit: 50,
    }),
    [threadId, locationId, focusItemId, focusItemCreatedAt]
  );

  const getThreadV3Query = useGetThreadV3Query({
    request: v3Request,
    options: {
      retry: retryLimit !== undefined ? retryLimit : 1,
      enabled: enabled !== undefined ? enabled && !getUnifiedThreadV4Enabled : !getUnifiedThreadV4Enabled,
      ...(cacheTime !== undefined && { cacheTime }),
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            thread: {
              ...page.thread,
              items: page.thread.messages.map((message) => ({
                smsMessage: message,
              })),
            },
          })),
        };
      },
    },
  });

  const v4Request = useMemo(
    () => ({
      threadId,
      locationId,
      pageSize: 50,
      filter: {
        focusThreadItem: focusItemId,
        focusCreatedAt: focusItemCreatedAt,
        showDeleted: true,
      },
    }),
    [threadId, locationId, focusItemId, focusItemCreatedAt]
  );

  const getUnifiedThreadV4Query = useGetUnifiedThreadV4Query({
    request: v4Request,
    options: {
      retry: retryLimit !== undefined ? retryLimit : 1,
      enabled: enabled !== undefined ? enabled && getUnifiedThreadV4Enabled : getUnifiedThreadV4Enabled,
      ...(cacheTime !== undefined && { cacheTime }),
    },
  });

  const getThreadQuery = getUnifiedThreadV4Enabled ? getUnifiedThreadV4Query : getThreadV3Query;

  const threadItems: ThreadItem[] = useMemo(() => {
    if (getUnifiedThreadV4Enabled) {
      // V4: items are already in correct format, just filter
      return (
        getUnifiedThreadV4Query.data?.pages
          .flatMap((page) =>
            (page.thread.items ?? []).filter(
              (item) =>
                isSmsMessageItem(item) ||
                (isCallRecordItem(item) && !!item.callRecord) ||
                (fusionEnabled && isAiActivityItem(item) && !!item.aiActivity)
            )
          )
          .reverse() ?? []
      );
    } else {
      // V3: wrap SMS as { smsMessage }
      return (
        getThreadV3Query.data?.pages
          .flatMap((page) => (page.thread.messages ?? []).map((sms) => ({ smsMessage: sms })))
          .reverse() ?? []
      );
    }
  }, [getUnifiedThreadV4Enabled, fusionEnabled, getUnifiedThreadV4Query.data, getThreadV3Query.data]);

  return {
    getThreadQuery,
    threadItems,
  };
};
